import React, {useCallback, useEffect, useRef, useState} from 'react';
import Portal from './Portal';
import debounce from 'throttle-debounce';
import deepEqual from 'deep-equal';

function equalDropdown(prev: any, next: any) {
  return !next.isRerender && deepEqual(prev.coords, next.coords);
}

type DropdownWrapperProps = {
  children: any,
  isRevert?: boolean,
  isRerender?: boolean
  style: any
}

const DropdownWrapper = ({children, style, isRevert = false, isRerender = false}: DropdownWrapperProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({});

  const btnRef: any = React.createRef();

  const updateTooltipCoords = (button: any) => {
    const rect = button.getBoundingClientRect();
    setCoords({
      left: rect.x + rect.width / 2,
      top: rect.y + window.scrollY,
    });
  };

  if (!children) {
    return null;
  }
  if (React.Children.count(children) === 1) {
    return children;
  }

  const arrayChildren = React.Children.toArray(children);

  if (!isOpen) {
    return <div onClick={(e) => {
      e.stopPropagation();
      updateTooltipCoords(e.target);
      setIsOpen(!isOpen);
    }}>
      <div>
        {arrayChildren[0]}
      </div>
    </div>;
  }

  return <div>
    <div onClick={(e) => {
      e.stopPropagation();
      updateTooltipCoords(e.target);
      setIsOpen(!isOpen);
    }
    }
    ref={btnRef}
    >
      {arrayChildren[0]}
    </div>

    <Dropdown
      coords={coords}
      updateTooltipCoords={updateTooltipCoords}
      onClose={() => {
        setIsOpen(false);
      }}
      style={style}
      isRevert={isRevert}
      isRerender={isRerender}
    >
      {arrayChildren[1]}
    </Dropdown>
  </div>;
};

type DropdownProps = {
  onClose: any,
  children: any,
  coords: any,
  updateTooltipCoords: any,
  isRevert: boolean,
  isRerender: boolean,
  style: any
}

// eslint-disable-next-line react/display-name
const Dropdown = React.memo(({onClose, children, coords, updateTooltipCoords, isRevert, isRerender}: DropdownProps) => {
  const ref: any = useRef(null);

  const updateCoords: any = debounce.debounce(100, false, updateTooltipCoords);
  const escapeListener = useCallback((e: any) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, []);

  const clickListener = useCallback(
      (e: any) => {
        if (ref.current && !ref.current.contains(e.target)) {
          onClose();
        }
      },
      [ref.current],
  );

  useEffect(() => {
    window.addEventListener('resize', updateCoords);
    return () => window.removeEventListener('resize', updateCoords);
  }, []);

  useEffect(() => {
    document.addEventListener('click', clickListener);
    document.addEventListener('keyup', escapeListener);
    return () => {
      document.removeEventListener('click', clickListener);
      document.removeEventListener('keyup', escapeListener);
    };
  }, []);

  return (
    <Portal>
      <div
        ref={ref}
        style={{...(isRevert ? styles.popoverRevert : styles.popover), ...coords}}
        onClick={(e)=>e.stopPropagation()}
      >
        {React.cloneElement(children, {onClose: onClose})}
      </div>
    </Portal>
  );
}, equalDropdown);


const styles = {
  popover: {
    position: 'absolute',
    zIndex: 2000,
    transform: 'translate(-100px, 25px)',
  },
  popoverRevert: {
    position: 'absolute',
    zIndex: 2000,
    transform: 'translate(-100px, -100%)',
  },
};

export default DropdownWrapper;
